import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Spinner,
  Alert,
  Badge,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import toast from 'react-hot-toast';
import Header from "../Header";
import Footer from "../Footer";
import Fadeup from "../fadeup/Fadeup";

const base_url = process.env.REACT_APP_BASE_URL;

const ViewAuctions = () => {
  const { id } = useParams();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchAuctionById = async () => {
      const token = localStorage.getItem("token");
      try {
        const res = await fetch(
          `${base_url}/api/auction/getAuctionDetails/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const json = await res.json();
        if (!res.ok) {
          throw new Error(json.message || "Failed to fetch auction details");
        }
        setAuction(json.data);
      } catch (err) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchAuctionById();
  }, [id]);

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setEmail("");
  };

  // Handle modal open
  const handleShowModal = () => {
    setShowModal(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`${base_url}/api/auction/createPrivateAuction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          auction_id: id,
          allowed_users: [email.trim()],
        }),
      });

      const json = await res.json();

      if (res.ok && json.status === 200) {
        toast.success(json.message || "Private auction invite sent successfully!");
        handleCloseModal();
      } else {
        toast.error(json.message || "Failed to send private auction invite");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <>
        <Header />
        <Container className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </Container>
        <Footer />
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <Header />
        <Container className="my-5">
          <Alert variant="danger">{error}</Alert>
        </Container>
        <Footer />
      </>
    );
  }

  // Auction not found
  if (!auction) {
    return (
      <>
        <Header />
        <Container className="my-5">
          <Alert variant="warning">Auction not found.</Alert>
        </Container>
        <Footer />
      </>
    );
  }

  const details = auction?.details;
  const images = auction?.images || [];

  return (
    <>
      <Header />
      
      <section className="sec_pd">
        <div className="d-flex justify-content-between align-items-center mx-3 mb-3">
          <div>
            <Fadeup delay={0.3}>
              <h3 className="text-start mt-5  text-black fs_40">
                {auction?.title || "N/A"}
              </h3>
            </Fadeup>
           
          </div>
          
          {auction?.auction_type?.toLowerCase() === 'private' && (
            <Button 
              onClick={handleShowModal}
              className="ms-2 text-white btn-golden-glass"
              style={{ height: 'fit-content' }}
            >
              Add User
            </Button>
          )}
        </div>

        <div className="mt-2 text-dark mb-5">
          {/* Auction Status Info */}
          <div className="border p-4 mb-4" style={{borderRadius:"20px"}}>
            <strong><h4 className="my-4">Auction Information</h4></strong>
            <div className="row">
              <div className="col-md-4 mb-3">
                <strong>Auction Type:</strong>
                <p>{auction?.auction_type || "N/A"}</p>
              </div>
              <div className="col-md-4 mb-3">
                <strong>Status:</strong>
                <p>
                  <Badge bg={auction?.status === 1 ? "warning" : "secondary"}>
                    {auction?.status === 1 ? "Active" : "Inactive"}
                  </Badge>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <strong>Approval Status:</strong>
                <p>
                  <Badge bg="dark">{auction?.approval_status || "N/A"}</Badge>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <strong>Created On:</strong>
                <p>
                  {auction?.createdAt
                    ? new Date(auction.createdAt).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Details Section */}
          {details && (
            <div className="border p-4 mb-4" style={{borderRadius:"20px"}}>
              <strong><h4 className="my-4">Details</h4></strong>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <strong>Description:</strong>
                  <p>{details?.description || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Artist:</strong>
                  <p>{details?.artist || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Material:</strong>
                  <p>{details?.material || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Carbon Dating:</strong>
                  <p>{details?.carbon_dating || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Dimensions (HxWxD):</strong>
                  <p>
                    {details?.height && details?.width && details?.depth && details?.measurement_units
                      ? `${details.height} x ${details.width} x ${details.depth} ${details.measurement_units}`
                      : "N/A"}
                  </p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Origin:</strong>
                  <p>{details?.item_origin || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Object Type:</strong>
                  <p>{details?.object_type || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Markings:</strong>
                  <p>{details?.markings || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Damage:</strong>
                  <p>{details?.damage || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Repair:</strong>
                  <p>{details?.repair || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Ownership History:</strong>
                  <p>{details?.ownership_history || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Appraisals:</strong>
                  <p>{details?.appraisals || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Price:</strong>
                  <p>
                    {details?.currency && details?.price
                      ? `${details.price} ${details.currency}`
                      : "N/A"}
                  </p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Year Paid:</strong>
                  <p>{details?.year_paid || "N/A"}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Notes:</strong>
                  <p>{details?.notes || "N/A"}</p>
                </div>

                {/* Certificates */}
                {details?.certificate1 && (
                  <div className="col-md-4 mb-3">
                    <strong>Certificate 1:</strong><br />
                    <a 
                      href={`${base_url}/uploads/${details.certificate1}`} 
                      className="btn btn-golden-glass text-white mt-2" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      View PDF
                    </a>
                  </div>
                )}
                {details?.certificate2 && (
                  <div className="col-md-4 mb-3">
                    <strong>Certificate 2:</strong><br />
                    <a 
                      href={`${base_url}/uploads/${details.certificate2}`} 
                      className="btn btn-golden-glass text-white mt-2" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      View PDF
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Images Section */}
          {images?.length > 0 && (
            <>
              <h4 className="mt-4">Images</h4>
              <div className="row g-3 mt-3">
                {images.map((image, index) => (
                  <div
                    key={image.id || index}
                    className="col-12 col-sm-6 col-md-4 col-lg-4"
                  >
                    <img
                      src={image.image}
                      className="img-fluid rounded border p-1"
                      alt={image.image_type}
                      style={{
                        height: "300px",
                        width: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <div className="text-center mt-2">
                      <small className="text-muted">
                        {image.image_type === 'main' ? 'Main' : `Side ${image.image_type.split('_')[1]}`}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Fallback if no images in array but details.image exists */}
          {images.length === 0 && details?.image && (
            <>
              <h4 className="mt-4">Image</h4>
              <div className="row g-3 mt-3">
                <div className="col-12 col-sm-6 col-md-4 col-lg-4">
                  <img
                    src={details.image}
                    className="img-fluid rounded border p-1"
                    alt="Auction Item"
                    style={{
                      height: "300px",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Add User Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add User to Private Auction</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter user's email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={submitting}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="dark" 
              className="text-white"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button  
              className="text-white btn-golden-glass"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Adding...
                </>
              ) : (
                "Add User"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </section>
      
      <Footer />
    </>
  );
};

export default ViewAuctions;